import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { Modal, Box, TextField } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import { API } from "../../apis/api";
import {
  API_RESOURCE_URLS,
  ASN_STATUS,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
  DATE_FORMAT,
  USER_OPERATIONS,
  RESOURCE_TYPE,
  JLR_PLANT,
  PANTNAGAR_PLANT_NAME,
  APPLICATION_VARIANTS,
  ZPL_ASN_TEMPLATE,
} from "../../constants";
import defaultStyles from "./ChassisDrop.module.css";
import jlrStyles from "./ChassisDropJlr.module.css";
import "../../cssModules/switch.css";
import {
  AggregateStatusIndicator,
  DropToolBar,
  BackButton,
  DayWiseFinishedGoodsLineTransferDisplay,
  CriticalMaterialIndicator,
} from "..";
import { statusStringMap } from "../../staticData";
import { AGGREGATE_STATUS } from "../../constants";
import {
  loadAsnDates,
  compareByDateAndSerialNumber,
  formatDataToIncludeColour,
  getAggregateStatusAndModifiedAtAndComment,
} from "../../utils/tableUtils";
import { reasons, agencies } from "../../staticData";
import { useAjaxCallbackWithIntervals } from "../../hooks";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";
import {
  buildErrorMessage,
  fetchAllLinedOutAsnsForPlantAndLine,
} from "../../apis/calls";
import { usePlantAndLinesStore } from "../../providers/PlantAndLinesStore/PlantAndLinesStore";
import { useHistory } from "react-router-dom";
import { withAllowedOperationsProvider } from "../../hocs";
import { useAuthorizationContext } from "../AuthorizationProvider/AuthorizationProvider";
import {
  downloadCsv,
  setStylesAccordingToPlant,
  checkForPendingAgsnsOrAsn,
} from "../../utils/utils";
import Table from "../Table/Table";
import {
  BulkOptionsToolbar,
  CommentFormModal,
  CarryForwardModal,
  LineTransferModal,
  ReprintWarningDialog,
} from "..";
import config from "../../config/config";
import AggregateAvailableErrorPopup from "../AggregateAvailableErrorPopup/AggregateAvailableErrorPopup";

const ChassisDrop = ({
  hasActiveEdits,
  setHasActiveEdits,
  handleCountChange,
}) => {
  const { user } = useAuthorizationContext();
  const ROW_HEIGHT = 38;
  const RESOURCE_OPERATION = USER_OPERATIONS.UPDATE_CHASSIS_PLAN_ASN;
  const {
    showError,
    showInternalError,
    showSuccess,
    showInformation,
    showLoading,
    closePopUp,
    showPopup,
  } = usePopupManager();
  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;
  const [isPrintingEnabled, setIsPrintingEnabled] = useState(
    plant?.label === PANTNAGAR_PLANT_NAME,
  );
  const [isReprintConfirmationOpen, setIsReprintConfirmationOpen] = useState(
    false,
  );
  const [asnsToReprint, setAsnsToReprint] = useState([]);
  const [openPbsDialog, setOpenPbsDialog] = useState(false);
  const [selectedPbsRow, setSelectedPbsRow] = useState(null);
  const [biwSerialNumber, setBiwSerialNumber] = useState("");
  const [scannedVc, setScannedVc] = useState("");
  const vcInputRef = useRef("");

  const latestLineValueRef = useRef(line.value);
  const latestPlantValueRef = useRef(plant.value);
  const [styles, setStyles] = useState(defaultStyles);

  const history = useHistory();
  const { APPLICATION_VARIANT, RFD_VALIDATION_ENABLEMENT } = config;

  const isRfdValidationEnabled = useMemo(() => {
    if (!RFD_VALIDATION_ENABLEMENT || !line.value) return false;
    const enabledLines = Array.isArray(RFD_VALIDATION_ENABLEMENT)
      ? RFD_VALIDATION_ENABLEMENT
      : String(RFD_VALIDATION_ENABLEMENT)
          .split(",")
          .map((id) => id.trim());
    return enabledLines.some((id) => String(id) === String(line.value));
  }, [RFD_VALIDATION_ENABLEMENT, line.value]);
  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });
  const [isPullbackModalOpen, setIsPullbackModalOpen] = useState(false);
  const [asnDates, setAsnDates] = useState([]);
  const [activeAsnDate, setActiveAsnDate] = useState(null);
  const [enableRDFButton, setEnableRDFButton] = useState(true);
  const [selectedOptionalColNames, setSelectedOptionalColNames] = useState([]);
  const [hasPendingBacklogAsns, setHasPendingBacklogAsns] = useState(false);
  const [hasButtonEnable, setHasButtonEnable] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [allLineoutsData, setAllLineoutsData] = useState({
    loading: false,
    data: [],
  });
  const [asnCount, setAsnCount] = useState(0);
  const setCount = (count) => {
    setAsnCount(count);
  };
  const closePullBackModal = () => setIsPullbackModalOpen(false);
  const openPullBackModal = (selectedRows) => {
    setIsPullbackModalOpen(true);
    setSelectedRows(selectedRows);
  };

  const [isPlanErrorsOpen, setIsPlanErrorsOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState();

  const isLucknowProlifePlant = plant?.label === "Lucknow-Prolife";
  const parentName = isLucknowProlifePlant ? "Parent" : "VC";
  const nextMonthLabel = isLucknowProlifePlant ? "ENG" : "VIN";
  const fixedColumns = [
    {
      width: 150,
      title: "ASN",
      field: "asn",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: parentName,
      field: "vc",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: "Description",
      field: "vcDescription",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: "Shift",
      field: "shiftName",
      enableSearch: true,
      enableFilter: true,
      render: ({ shiftName }) => (
        <div style={{ textAlign: "center" }}>
          {shiftName && (
            <div
              className={
                JLR_PLANT === plant.label ? styles.jlrshift : styles.shift
              }
            >
              {shiftName}
            </div>
          )}
        </div>
      ),
    },
    {
      width: 160,
      title: "Planned FDT",
      field: "plannedDropTimestamp",
      render: (row) =>
        row.plannedDropTimestamp
          ? moment(row.plannedDropTimestamp).format("hh:mm A DD/MM/YYYY")
          : null,
      enableSearch: true,
      enableFilter: true,
    },
    APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4
      ? {
          width: 160,
          title: "Aggregate Status",
          field: "aggregate",
          render: (row) => {
            return row.materialGroupClusters.length > 0 ? (
              <AggregateStatusIndicator rowData={row} />
            ) : null;
          },
        }
      : null,
    {
      width: 140,
      title: `${parentName} Status`,
      field: "vcStatus",
      render: (row) => {
        if (row.status === ASN_STATUS.PLANNED)
          return <span style={{ opacity: 0.5 }}>Pending</span>;
        if (row.edited) return "Pending";
        return statusStringMap[row.status];
      },
      enableSearch: true,
      enableFilter: true,
    },
    isLucknowProlifePlant
      ? null
      : {
          width: 200,
          title: "Evr Number",
          field: "evrNumber",
          enableSearch: true,
          enableFilter: true,
        },
  ].filter((c) => c);
  const dropTableColumns = [
    {
      width: 200,
      title: "PowerSteering",
      field: "powerSteering",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: "BC No.",
      field: "buildCardNumber",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: "Defence",
      field: "defence",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: "Market",
      field: "market",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "Category",
      field: "category",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "Country",
      field: "country",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 110,
      title: "FBV",
      field: "fbv",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "Colour",
      field: "colour",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "Major Model",
      field: "majorModel",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 160,
      title: "Critical Material",
      field: "pendingParts",
      render: (row) => {
        if (!row.pendingParts?.length) {
          return null;
        }

        return (
          <CriticalMaterialIndicator
            vc={row.vc}
            pendingParts={row.pendingParts}
          />
        );
      },
    },
    {
      width: 200,
      title: "SAP Production Month",
      field: "sapProductionMonth",
      enableSearch: true,
      enableFilter: true,
      render: (row) => {
        const val = row.sapProductionMonth;
        // Check for null, undefined, or empty string
        if (
          !val ||
          val === "null" ||
          val === "undefined" ||
          val.trim() === "" ||
          val === " null null"
        ) {
          return "";
        }
        // Parse using the correct format: "M-YYYY"
        const date = moment(val, "M-YYYY", true);
        return date.isValid() ? date.format("MM-YYYY") : "";
      },
    },
    {
      width: 140,
      title: "Tyre",
      field: "tyre",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "Other Remark",
      field: "otherRemark",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: "Vc Alias",
      field: "rootMaterialAlias",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 170,
      title: "VC Material",
      field: "rootMaterial",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 300,
      title: "VC Material Description",
      field: "rootMaterialDescription",
      enableSearch: true,
      enableFilter: true,
    },
  ];
  const [selectedAsns, setSelectedAsns] = useState([]);
  const [isAssignShiftFormOpen, setIsAssignShiftFormOpen] = useState(false);
  const [isNextMonthFormOpen, setIsNextMonthFormOpen] = useState(false);
  const [isAddTyerAndOthersOpen, setIsAddTyerAndOthersOpen] = useState(false);

  const closeAssignShiftForm = () => setIsAssignShiftFormOpen(false);
  const closeNextMonthVinForm = () => setIsNextMonthFormOpen(false);
  const closeAddTyerAndOthers = () => setIsAddTyerAndOthersOpen(false);

  const [isLineOutModalOpen, setIsLineOutModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isLineTransferModalOpen, setIsLineTransferModalOpen] = useState(false);
  const [
    isLineTransferAsnDisplayOpen,
    setIsLineTransferAsnDisplayOpen,
  ] = useState(false);
  const [lineSelectedForTransfer, setLineSelectedForTransfer] = useState(null);

  const openModal = (selectedRows) => {
    setIsLineOutModalOpen(true);
    setSelectedRows(selectedRows);
  };
  const openLineTransferModal = async (selectedRows) => {
    try {
      showLoading();
      const response = await API.get(
        API_RESOURCE_URLS.getLinesForTransfer(plant.value, line.value),
      );
      const lineDetails = response.data;
      setTransferLines(lineDetails);
      setIsLineTransferModalOpen(true);
      setSelectedRows(selectedRows);
      closePopUp();
    } catch (error) {
      console.error(buildErrorMessage(error));
      showError({
        contextText: `Failed to Fetch Lines for Asn/Agsn Transfer`,
        error: error,
      });
      setTransferLines(null);
      closePopUp();
    }
  };
  const closeModal = () => setIsLineOutModalOpen(false);
  const closeLineTransferModal = () => setIsLineTransferModalOpen(false);
  const closeLinetransferAnsDisplay = () =>
    setIsLineTransferAsnDisplayOpen(false);

  const [carryForwardAsnDetails, setCarryForwardAsnDetails] = useState({});
  const [carryForwardAsnCount, setCarryForwardAsnCount] = useState({});

  const [isCarryForwardModalOpen, setIsCarryForwardModalOpen] = useState(false);
  const openCarryForwardModal = () => setIsCarryForwardModalOpen(true);
  const closeCarryForwardModal = () => setIsCarryForwardModalOpen(false);
  const redirectToApplicationLandingPage = () => {
    history.push("/");
  };

  const [shiftAvailability, setShiftAvailability] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shopId, setShopId] = useState(null);
  const [transferLines, setTransferLines] = useState(null);

  const fetchLastAsnGeneratedDate = async (plant, line) => {
    try {
      const response = await API.get(
        API_RESOURCE_URLS.LAST_ASN_GENERATED_DATE,
        {
          params: {
            plant,
            line,
          },
        },
      );
      return response.data[0].lastDate;
    } catch (error) {
      console.error(buildErrorMessage(error));
      showError({
        contextText: `Failed to Fetch Last Asn/Agsn Generated Date`,
        error: error,
      });
      return null;
    }
  };
  const fetchPendingCriticalParts = async (plant, line, date) => {
    const formattedDate = date ? date.format("YYYY-MM-DD") : "";
    try {
      const response = await API.get(
        API_RESOURCE_URLS.chassisCriticalPartsPending(
          plant,
          line,
          formattedDate,
        ),
      );
      return response;
    } catch (error) {
      console.error(buildErrorMessage(error));
      return { data: [] };
    }
  };

  const handleLineSelectionForLineTransfer = async (selectedLineName) => {
    const selectedLine = transferLines.find(
      (line) => line.lineName === selectedLineName,
    );
    if (!selectedLine) {
      showError({ contextText: "Selected line not found." });
      return;
    }
    const lastAsnDate = await fetchLastAsnGeneratedDate(
      plant.value,
      selectedLine.line,
    );
    if (lastAsnDate && moment(lastAsnDate).isBefore(activeAsnDate, "day")) {
      showError({
        contextText: DISPLAY_MESSAGES.LINE_TRANSFER_ERROR,
        info: `Sequence for ${selectedLineName} is not generated for ${activeAsnDate.format(
          "DD-MM-YYYY",
        )} .`,
      });
      setIsLineTransferModalOpen(false);
      return;
    }
    setLineSelectedForTransfer(selectedLine);
    setIsLineTransferAsnDisplayOpen(true);
  };

  const addFlagsAndFormatDataForRender = (asns, pendingPartsMap = {}) => {
    if (asns.length === 0) return [];
    const rows = asns.map((asn) => {
      const { asnId, materialGroupClusters, ...rest } = asn;
      const updatedMaterialGroupClusters = materialGroupClusters.map(
        ({ name, materials }) => ({
          name,
          materials: materials.map((material) =>
            getAggregateStatusAndModifiedAtAndComment(material),
          ),
        }),
      );

      const hasUnAvailableAggregates = updatedMaterialGroupClusters
        .map((group) => group.materials[0].aggregateStatus)
        .some(
          (aggregateStatus) => aggregateStatus === AGGREGATE_STATUS.LINE_OUT,
        );

      return {
        asnId,
        vcStatus: statusStringMap[asn.status],
        ...rest,
        materialGroupClusters: [...updatedMaterialGroupClusters],
        hasUnAvailableAggregates,
        pendingParts: pendingPartsMap[asnId] || [], // Attached safely
      };
    });
    rows.sort((a, b) =>
      compareByDateAndSerialNumber(a, b, "date", "serialNumber"),
    );
    const dropSequenceUpdatedRows = updateDropEligibleAsnForAsns(rows);
    return dropSequenceUpdatedRows;
  };

  const prepareJsonHeaderAndValueFormats = (aggregatePartNames) => {
    const vcAggregateHeaderAndValueFormats = aggregatePartNames.map(
      (partName) => ({
        label: partName + " Status",
        value: (row) =>
          row.materialGroupClusters.filter(
            (group) => group.name === partName,
          )[0]?.materials[0]?.aggregateStatus || "unavailable",
      }),
    );

    const vcOptionalAttributesArray = [
      {
        label: "Major Model",
        value: (row) => row.majorModel,
      },
      {
        label: "Power Steering",
        value: (row) => row.powerSteering,
      },
      {
        label: "Build Card Number",
        value: (row) =>
          row.buildCardNumber ? `="${row.buildCardNumber}"` : "",
      },
      {
        label: "Market",
        value: (row) => row.market,
      },
      {
        label: "Category",
        value: (row) => row.category,
      },
      {
        label: "Country",
        value: (row) => row.country,
      },
      {
        label: "Defence",
        value: (row) => row.defence,
      },
      {
        label: "FBV",
        value: (row) => row.fbv,
      },

      {
        label: "FBV Vendor Code",
        value: (row) => row.fbvVendorCode,
      },
      {
        label: "FBV Vendor Name",
        value: (row) => row.fbvVendorName,
      },
      {
        label: "Colour",
        value: (row) => row.colour,
      },
      {
        label: "SAP Production Month ",
        value: (row) => {
          const val = row.sapProductionMonth;
          if (!val || val === "null" || val === "undefined") return "";
          const check = moment(val);
          return check.isValid() ? check.format("MM-YYYY") : "";
        },
      },
      {
        label: "Tyre",
        value: (row) => row.tyre,
      },
      {
        label: "Other Remark",
        value: (row) => row.otherRemark,
      },
    ];

    const vcHeaderAndValueFormats = [
      {
        label: "ASN",
        value: (row) => row.asn,
      },
      {
        label: parentName,
        value: (row) => row.vc,
      },
      {
        label: `${parentName} Description`,
        value: (row) => row.vcDescription?.replace(/[\n\r,]/g, ""),
      },
      {
        label: "Planned On",
        value: (row) => row.date,
      },
      {
        label: `${parentName} Status`,
        value: (row) => row.status,
      },
      {
        label: "Original Planned Date",
        value: (row) => row.originalDate,
      },
      {
        label: "Family",
        value: (row) => row.family,
      },
      {
        label: "Order Frequency",
        value: (row) => row.orderFrequency,
      },
      {
        label: "Line",
        value: (row) => row.line,
      },
      {
        label: "Shift",
        value: (row) => row.shiftName,
      },
      {
        label: "Planned FDT",
        value: (row) =>
          row.plannedDropTimestamp
            ? moment(row.plannedDropTimestamp).format("hh:mm A DD/MM/YYYY")
            : null,
      },
    ];
    if (APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4) {
      return [
        ...vcHeaderAndValueFormats,
        ...vcOptionalAttributesArray.map((record) => record),
        ...vcAggregateHeaderAndValueFormats,
      ];
    } else {
      return [...vcHeaderAndValueFormats, ...vcAggregateHeaderAndValueFormats];
    }
  };
  const openAssignShiftForm = (selectedRows) => {
    setIsAssignShiftFormOpen(true);
    setSelectedAsns(selectedRows);
  };

  const openNextMonthForm = (selectedRows) => {
    setIsNextMonthFormOpen(true);
    setSelectedAsns(selectedRows);
  };

  const openAddTyerAndOthers = (selectedRows) => {
    setIsAddTyerAndOthersOpen(true);
    setSelectedAsns(selectedRows);
  };

  const handleCarryForwardAsn = (selectedRows = []) => {
    const allPlannedAsns = rowsData.data.filter(
      (asn) => asn.status === ASN_STATUS.PLANNED,
    );
    if (allPlannedAsns.length === 0) return;

    const selectedPlannedAsns = (selectedRows || []).filter(
      (asn) => asn.status === ASN_STATUS.PLANNED,
    );

    if (
      !selectedRows ||
      selectedRows.length === 0 ||
      selectedPlannedAsns.length === 0
    ) {
      return showError({
        contextText: "Please select a planned ASN row for carry forward",
      });
    }

    setHasButtonEnable(false);

    let targetAsn = allPlannedAsns[0];
    let count = allPlannedAsns.length;

    const selectedIndex = allPlannedAsns.findIndex(
      (asn) => asn.asnId === selectedPlannedAsns[0].asnId,
    );
    if (selectedIndex !== -1) {
      targetAsn = allPlannedAsns[selectedIndex];
      count = allPlannedAsns.length - selectedIndex;
    } else {
      targetAsn = selectedPlannedAsns[0];
    }

    setCarryForwardAsnDetails(targetAsn);
    setCarryForwardAsnCount(count);
    openCarryForwardModal();
  };
  const generateAndDownloadAsnCsvReport = (filteredData) => {
    try {
      if (!rowsData.loading) {
        const dateValue = activeAsnDate.isBefore(
          moment().format(DATE_FORMAT.ISO),
        )
          ? "PreviousDates"
          : activeAsnDate.format("YYYYMMDD");
        const jsonData = filteredData;

        const aggregatePartNames =
          rowsData.data[0]?.materialGroupClusters.map((group) => group.name) ||
          [];

        const { parseAsync } = require("json2csv");
        const fields = prepareJsonHeaderAndValueFormats(aggregatePartNames);
        const opts = { fields, quote: "" };
        parseAsync(jsonData, opts)
          .then((csv) => {
            const exportedFilename =
              "AsnReport_" +
              plant.label +
              "_" +
              line.label +
              "_" +
              dateValue +
              ".csv";
            downloadCsv(csv, exportedFilename);
          })
          .catch((error) =>
            console.error(
              "Failed to download ASNs report, error:" +
                buildErrorMessage(error),
            ),
          );
      }
    } catch (error) {
      console.error(
        "Failed to download ASNs report, error:" + buildErrorMessage(error),
      );
      showInternalError({ error: error });
    }
  };

  const reloadData = () => {
    return loadDataForDateAndPlantAndLine(
      activeAsnDate,
      plant.value,
      line.value,
    );
  };
  const checkForPendingAsns = useCallback(
    (selectedRows) => {
      const allPlannedAsns = rowsData.data.filter(
        (asn) => asn.status === ASN_STATUS.PLANNED,
      );
      if (allPlannedAsns.length && selectedRows.length && enableRDFButton) {
        const valid = selectedRows.every(
          (selectedRow, index) =>
            selectedRow.asnId === allPlannedAsns[index].asnId,
        );
        return valid;
      }
      return false;
    },
    [rowsData.data, enableRDFButton],
  );

  const getDropEligibleAsnId = (asnData) => {
    const asns = asnData.map((asn) => ({
      ...asn,
      isDropEligible: false,
    }));
    const eligibleAsns = asns.filter(
      (asn) => asn.status === ASN_STATUS.PLANNED,
    );

    const dropEligibleAsn = eligibleAsns[0];
    return dropEligibleAsn ? dropEligibleAsn.asnId : null;
  };

  const updateDropEligibleAsnForAsns = (asns) => {
    const dropEligibleAsnId = getDropEligibleAsnId(asns);
    return asns.map((asn) => {
      const isDropEligible = asn.asnId === dropEligibleAsnId;
      return { ...asn, isDropEligible };
    });
  };

  const fetchAsnsForDate = (dateString, selectedPlant, selectedChassisLine) =>
    API.get(API_RESOURCE_URLS.VC_ASN, {
      params: {
        from: dateString,
        to: dateString,
        plant: selectedPlant,
        line: selectedChassisLine,
        status: user.viewableChassisDropStatuses,
      },
    });

  const fetchBacklogAsnsBeforeDate = (
    date,
    selectedPlant,
    selectedChassisLine,
  ) =>
    API.get(API_RESOURCE_URLS.VC_ASN, {
      params: {
        before: date.format(DATE_FORMAT.ISO),
        plant: selectedPlant,
        line: selectedChassisLine,
        status: user.viewableChassisDropStatuses,
      },
    });

  const getPendingBacklogAsnsBeforeDate = (
    date,
    selectedPlant,
    selectedChassisLine,
  ) =>
    API.get(API_RESOURCE_URLS.VC_ASN, {
      params: {
        before: date.format(DATE_FORMAT.ISO),
        status: [ASN_STATUS.PLANNED],
        plant: selectedPlant,
        line: selectedChassisLine,
      },
    });

  const fetchAsnsBasedOnDate = (date, selectedPlant, selectedChassisLine) => {
    const today = moment();
    const dateString = date.format(DATE_FORMAT.ISO);
    if (date.isBefore(today, "day"))
      return fetchBacklogAsnsBeforeDate(
        today,
        selectedPlant,
        selectedChassisLine,
      );
    return fetchAsnsForDate(dateString, selectedPlant, selectedChassisLine);
  };

  const setPreviousPendingAsnsCheck = async (
    date,
    selectedPlant,
    selectedChassisLine,
  ) => {
    const today = moment();
    if (!date || date.isBefore(today, "day"))
      return setHasPendingBacklogAsns(false);
    try {
      const response = await getPendingBacklogAsnsBeforeDate(
        date,
        selectedPlant,
        selectedChassisLine,
      );
      if (
        selectedPlant !== latestPlantValueRef.current ||
        selectedChassisLine !== latestLineValueRef.current
      )
        return;
      return setHasPendingBacklogAsns(response.data.length > 0);
    } catch (err) {
      console.error(buildErrorMessage(err));
      showInternalError({ error: err });
    }
  };

  const [pbsDataMap, setPbsDataMap] = useState({});

  const loadPbsDataForPantnagar = useCallback(async () => {
    if (isRfdValidationEnabled) {
      try {
        const response = await API.get(
          API_RESOURCE_URLS.getUpdatedPbsDroppingData(plant.value),
        );
        const pbsOutData = response.data || [];
        const pbsOutMap = {};
        pbsOutData.forEach((item) => {
          if (item.asnId) {
            pbsOutMap[item.asnId] = item;
          }
          if (item.asn) {
            pbsOutMap[item.asn] = item;
          }
        });
        setPbsDataMap(pbsOutMap);
      } catch (error) {
        console.error("Failed to load PBS data:", error);
      }
    }
  }, [isRfdValidationEnabled, plant.value]);

  useEffect(() => {
    if (isRfdValidationEnabled && rowsData.data.length > 0) {
      loadPbsDataForPantnagar();
    }
  }, [isRfdValidationEnabled, rowsData.data, loadPbsDataForPantnagar]);

  const loadVcAsnForDateAndPlantAndLine = async (
    date,
    selectedPlant,
    selectedChassisLine,
  ) => {
    if (!date)
      return setRowsData({
        loading: false,
        data: [],
        filteredData: [],
      });
    try {
      setRowsData((prevState) => ({ ...prevState, loading: true }));

      const [asnResponse, pendingPartsResponse] = await Promise.all([
        fetchAsnsBasedOnDate(date, selectedPlant, selectedChassisLine),
        fetchPendingCriticalParts(selectedPlant, selectedChassisLine, date),
      ]);

      if (
        selectedPlant !== latestPlantValueRef.current ||
        selectedChassisLine !== latestLineValueRef.current
      )
        return setRowsData((prevState) => ({ ...prevState, loading: false }));

      const pendingPartsMap = {};
      const pendingData = pendingPartsResponse?.data || [];
      pendingData.forEach((item) => {
        if (item.asnId) {
          pendingPartsMap[item.asnId] = item.pendingParts || [];
        }
      });

      const formattedData = addFlagsAndFormatDataForRender(
        asnResponse.data,
        pendingPartsMap,
      );
      const data = formatDataToIncludeColour(formattedData);

      setRowsData({
        loading: false,
        data: data.map((item) => ({
          ...item,
          vcStatus: statusStringMap[item.status],
        })),
        filteredData: data.map((item) => ({
          ...item,
          vcStatus: statusStringMap[item.status],
        })),
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      showInternalError({ error: err });
      setRowsData((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const checkForPendingAsnAndLoadData = async (date) => {
    const selectedPlant = plant.value;
    const selectedChassisLine = line.value;

    await setPreviousPendingAsnsCheck(date, selectedPlant, selectedChassisLine);
    return loadDataForDateAndPlantAndLine(
      date,
      selectedPlant,
      selectedChassisLine,
    );
  };

  const loadAllLineOutsForPlantAndLine = async (
    selectedPlant,
    selectedChassisLine,
  ) => {
    setAllLineoutsData((prevState) => ({ ...prevState, loading: true }));
    try {
      const response = await fetchAllLinedOutAsnsForPlantAndLine(
        selectedPlant,
        selectedChassisLine,
      );
      if (
        selectedPlant !== latestPlantValueRef.current ||
        selectedChassisLine !== latestLineValueRef.current
      )
        return setAllLineoutsData((prevState) => ({
          ...prevState,
          loading: false,
        }));

      setAllLineoutsData({ loading: false, data: [...response.data] });
      setHasButtonEnable(true);
    } catch (err) {
      console.error(buildErrorMessage(err));
      showInternalError({ error: err });
      setAllLineoutsData((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const checkForInvalidSequence = (asns) => {
    const errors = [];
    const indexOfFirstPlannedAsn = asns.findIndex(
      (asn) => asn.status === ASN_STATUS.PLANNED,
    );
    if (indexOfFirstPlannedAsn >= 0) {
      const firstDroppedAnsIndexAfterPlannedAsn = asns
        .slice(indexOfFirstPlannedAsn + 1)
        .findIndex((asn) => asn.status === ASN_STATUS.READY_FOR_DROP);
      if (firstDroppedAnsIndexAfterPlannedAsn >= 0)
        errors.push(DISPLAY_MESSAGES.DROP_SEQUENCE_VIOLATION_WARNING);
    }

    const unCommentedLineOuts = asns
      .filter((asn) => asn.status === ASN_STATUS.LINE_OUT && asn.edited)
      .filter((asn) => !asn.reason || !asn.agency);

    if (unCommentedLineOuts.length > 0)
      errors.push(
        `Reason and Agency details are required when lining-out a ${parentName}.`,
      );
    return errors;
  };

  const loadDataForDateAndPlantAndLine = (
    date,
    selectedPlant,
    selectedChassisLine,
  ) => {
    return loadVcAsnForDateAndPlantAndLine(
      date,
      selectedPlant,
      selectedChassisLine,
    ).then(() =>
      loadAllLineOutsForPlantAndLine(selectedPlant, selectedChassisLine),
    );
  };

  const isShiftAssigned = (rows) =>
    rows.every((row) => {
      return row.shiftName !== null;
    });

  const getAsnsPayloadWithShiftsAssigned = (
    selectedAsns,
    selectedShiftValue,
  ) => {
    return selectedAsns.map((asn) => ({
      materialId: asn.id,
      shiftId: selectedShiftValue,
    }));
  };

  const getAsnsPayloadForNextMonthVIN = (selectedAsns, selectedShiftValue) => {
    return selectedAsns.map((asn) => ({
      materialId: asn.id,
      nextMonthVin: selectedShiftValue,
    }));
  };

  const getAsnsPayloadForTyreAndOther = (selectedAsns, selectedValues) => {
    return selectedAsns.map((asn) => ({
      materialId: asn.id,
      tyre: selectedValues.tyre,
      otherRemark: selectedValues.otherRemark,
    }));
  };

  const assignShift = async (asns, selectedShiftValue) => {
    const asnPayload = getAsnsPayloadWithShiftsAssigned(
      asns,
      selectedShiftValue,
    );
    await API.patch(
      API_RESOURCE_URLS.assignShiftToMaterial(plant.value, line.value),
      asnPayload,
    );
  };

  const nextMonthVINRequest = async (asns, selectedShiftValue) => {
    const asnPayload = getAsnsPayloadForNextMonthVIN(asns, selectedShiftValue);
    await API.patch(
      API_RESOURCE_URLS.assignNextMonthVin(plant.value, line.value),
      asnPayload,
    );
  };

  const activeTyreAndOthersRequest = async (asns, selectedValues) => {
    const asnPayload = getAsnsPayloadForTyreAndOther(asns, selectedValues);
    await API.patch(
      API_RESOURCE_URLS.addTyreAndOtherRemark(plant.value, line.value),
      asnPayload,
    );
  };

  const getAsnsPayloadWithBulkShiftsAssigned = (
    sortedRequiredShifts,
    requiredAvailableAsns,
    assignmentsToShiftLabelsMap,
  ) => {
    const payloadWithShiftsAssigned = [];

    const requiredAvailableAsnsPayload = requiredAvailableAsns.map(
      ({ asnId }) => ({ asnId }),
    );

    for (const shift of sortedRequiredShifts) {
      const asnsWithCurrentShiftAssigned = requiredAvailableAsnsPayload
        .slice(
          payloadWithShiftsAssigned.length,
          payloadWithShiftsAssigned.length +
            assignmentsToShiftLabelsMap[shift.label],
        )
        .map((data) => ({ ...data, shiftId: shift.value }));
      payloadWithShiftsAssigned.push(...asnsWithCurrentShiftAssigned);
    }
    return payloadWithShiftsAssigned;
  };

  const bulkAssignShift = async (
    sortedRequiredShifts,
    requiredAvailableAsns,
    assignmentsToShiftLabelsMap,
  ) => {
    showLoading();
    const asnsPayload = getAsnsPayloadWithBulkShiftsAssigned(
      sortedRequiredShifts,
      requiredAvailableAsns,
      assignmentsToShiftLabelsMap,
    );

    await API.patch(
      API_RESOURCE_URLS.assignShiftToMaterial(plant.id, line.id),
      asnsPayload,
    );
    closePopUp();
  };

  const executeReprint = (rows) => {
    const labels = rows.map((row) => {
      const { asn, asnId, vc, colour, vcDescription } = row;

      return ZPL_ASN_TEMPLATE.replaceAll("{ASN}", asn || "")
        .replaceAll("{ASNID}", asnId || "")
        .replaceAll("{VC}", vc || "")
        .replaceAll("{COLOUR}", colour || "")
        .replaceAll("{DESC}", vcDescription || "")
        .replaceAll("{LINE}", line.label || "");
    });
    const zplstring = encodeURIComponent(labels.join("\n"));

    window.location.href = `nirmaan://?cmd=print&zplstring=${zplstring}`;
  };

  const reprintRows = (rows) => {
    if (isPrintingEnabled) {
      const hasDroppedAsn = rows.some(
        (row) => row.status === ASN_STATUS.DROPPED,
      );
      if (hasDroppedAsn) {
        setAsnsToReprint(rows);
        setIsReprintConfirmationOpen(true);
      } else {
        executeReprint(rows);
      }
    }
  };

  const handleConfirmReprint = () => {
    setIsReprintConfirmationOpen(false);
    executeReprint(asnsToReprint);
  };

  const handleCancelReprint = () => {
    setIsReprintConfirmationOpen(false);
    setAsnsToReprint([]);
  };

  const readyfordropRows = async (rows, bypassPbsCheck = false) => {
    const asns = rowsData.data;
    const errors = checkForInvalidSequence(asns);
    if (errors.length)
      return showError({
        info: errors,
        contextText: DISPLAY_MESSAGES.CLEAR_ERRORS,
      });

    if (!isShiftAssigned(rows))
      return showError({
        contextText: `Please assign a Shift for the ASN, before confirming it Ready for Drop.`,
      });

    if (isRfdValidationEnabled && !bypassPbsCheck) {
      const unvalidatedRows = rows.filter(
        (row) => !pbsDataMap[row.asn]?.serialNumber,
      );
      if (unvalidatedRows.length) {
        return showError({
          contextText: "BIW Validation Required",
          info: `Please complete BIW Serial Number validation before marking Ready for Drop.`,
        });
      }
    }

    try {
      showLoading();
      const payload = rows.map(({ id }) => ({
        id, // rm Id
      }));
      await API.patch(
        API_RESOURCE_URLS.vcAsnReadyForDrop(
          latestPlantValueRef.current,
          latestLineValueRef.current,
        ),
        {
          updates: payload,
        },
      );
      if (isPrintingEnabled) {
        const labels = rows.map((row) => {
          const { asn, asnId, vc, colour, vcDescription } = row;

          return ZPL_ASN_TEMPLATE.replaceAll("{ASN}", asn || "")
            .replaceAll("{ASNID}", asnId || "")
            .replaceAll("{VC}", vc || "")
            .replaceAll("{COLOUR}", colour || "")
            .replaceAll("{DESC}", vcDescription || "")
            .replaceAll("{LINE}", line.label || "");
        });
        const zplstring = encodeURIComponent(labels.join("\n"));

        window.location.href = `nirmaan://?cmd=print&zplstring=${zplstring}`;
      }

      await loadDataForDateAndPlantAndLine(
        activeAsnDate,
        plant.value,
        line.value,
      ); //reload data
      showSuccess({
        contextText: isLucknowProlifePlant
          ? DISPLAY_MESSAGES.PART_STATUS_UPDATED
          : DISPLAY_MESSAGES.VC_STATUS_UPDATED,
        info: "",
      });
    } catch (err) {
      if (err.response?.status === 400) {
        closePopUp();
        setIsPlanErrorsOpen(true);
        setUploadErrors(err.response.data || []);
      } else {
        console.error(buildErrorMessage(err));
        showInternalError({ error: err });
      }
    }
  };

  const lineoutRows = async (rows, reason, agency, comment) => {
    const asns = rowsData.data;
    const errors = checkForInvalidSequence(asns);
    if (errors.length)
      return showError({
        info: errors,
        contextText: DISPLAY_MESSAGES.CLEAR_ERRORS,
      });

    const editedrows = rows.map((row) => ({
      ...row,
      reason,
      agency,
      comment,
    }));
    try {
      setHasButtonEnable(false);
      showLoading();
      const payload = editedrows.map(({ asnId, reason, agency, comment }) => ({
        asnId,
        reason,
        agency,
        comment,
      }));

      const response = await API.patch(
        API_RESOURCE_URLS.VC_ASN_LINE_OUT(line.value),
        {
          updates: payload,
        },
      );

      await loadDataForDateAndPlantAndLine(
        activeAsnDate,
        plant.value,
        line.value,
      ); // Reload Data
      if (response.status === 200 && response.data?.length) {
        showSuccess({
          contextText: isLucknowProlifePlant
            ? DISPLAY_MESSAGES.PART_STATUS_LINEOUT
            : DISPLAY_MESSAGES.VC_STATUS_LINEOUT,
          info: "",
        });
      } else if (response.status === 202 && response.data?.length) {
        showInformation({
          contextText: response.data[0],
          info: "",
        });
      }
    } catch (err) {
      console.error(buildErrorMessage(err));
      showInternalError({ error: err });
    }
  };

  const lastRefreshedDate = useAjaxCallbackWithIntervals(
    rowsData.loading,
    hasActiveEdits,
    checkForPendingAsnAndLoadData,
    activeAsnDate,
    plant.value,
    line.value,
  );
  const pullBackAsn = async (selectedRows, reason, agency, comment) => {
    try {
      setHasButtonEnable(false);
      showLoading();
      const idsOfSelectedRows = selectedRows.map((res) => {
        return res.asnId;
      });
      const payload = {
        asnIds: idsOfSelectedRows,
        reason,
        agency,
        comment,
      };
      await API.patch(
        API_RESOURCE_URLS.agsnPullback(plant.value, line.value),
        payload,
      );
      setIsPullbackModalOpen(false);
      await loadDataForDateAndPlantAndLine(
        activeAsnDate,
        plant.value,
        line.value,
      );
      setHasButtonEnable(true);
      closePopUp();
      showSuccess({
        contextText: DISPLAY_MESSAGES.ASN_STATUS_PULLBACK_SUCCESS,
        info: "",
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      showPopup({
        error: err,
        contextText: DISPLAY_MESSAGES.ASN_STATUS_PULLBACK_FAILED,
      });
    }
  };

  const carryForwardAsn = async (data) => {
    try {
      showLoading();
      await API.patch(API_RESOURCE_URLS.carryForward(plant.value, line.value), {
        carryForwardFromAsnId: data.asnId,
      });
      setHasButtonEnable(true);
      closePopUp();
    } catch (error) {
      console.error(buildErrorMessage(error));
      showError({
        contextText: `Failed to Carry Forward ASN`,
        error: error,
      });
    }
  };
  const handleSubmitCarryForwardAsn = (data) => {
    return carryForwardAsn(data).then(() => {
      loadDataForDateAndPlantAndLine(activeAsnDate, plant.value, line.value);
    });
  };

  const openPbsDialogRef = useRef(false);

  const handleUpdatePbsDroppingStatus = useCallback(
    (selectedRows) => {
      if (selectedRows && selectedRows.length === 1) {
        const row = selectedRows[0];
        if (!pbsDataMap[row.asn]?.serialNumber && !openPbsDialogRef.current) {
          setSelectedPbsRow(row);
          setBiwSerialNumber("");
          setScannedVc("");
          openPbsDialogRef.current = true;
          setOpenPbsDialog(true);
        }
      }
    },
    [pbsDataMap],
  );

  const handleRowSelection = useCallback(
    (selectedRows) => {
      const futureDateRFDButtonDisable =
        activeAsnDate && activeAsnDate.isAfter(moment(), "day");
      if (
        isRfdValidationEnabled &&
        !futureDateRFDButtonDisable &&
        selectedRows.length &&
        selectedRows.every((row) => row.status === ASN_STATUS.PLANNED) &&
        checkForPendingAsns(selectedRows) &&
        !hasPendingBacklogAsns &&
        !openPbsDialogRef.current
      ) {
        handleUpdatePbsDroppingStatus(selectedRows);
      }
    },
    [
      activeAsnDate,
      isRfdValidationEnabled,
      checkForPendingAsns,
      hasPendingBacklogAsns,
      handleUpdatePbsDroppingStatus,
    ],
  );

  const handlePbsDialogClose = () => {
    openPbsDialogRef.current = false;
    setOpenPbsDialog(false);
    setSelectedPbsRow(null);
    setBiwSerialNumber("");
    setScannedVc("");
  };

  const handlePbsSubmit = async () => {
    if (!biwSerialNumber || !biwSerialNumber.trim()) {
      showError({
        contextText: "Validation Error",
        error: { message: "Please enter BIW Serial Number" },
      });
      return;
    }

    if (!scannedVc || !scannedVc.trim()) {
      showError({
        contextText: "Validation Error",
        error: { message: "Please scan the VC" },
      });
      return;
    }

    if (scannedVc.trim() !== selectedPbsRow.vc) {
      showError({
        contextText: "Validation Error",
        error: { message: "Scanned VC does not match the ASN VC" },
      });
      return;
    }

    if (plant.value && selectedPbsRow) {
      try {
        const payload = {
          asnId: selectedPbsRow.asnId,
          status: "READY FOR DROP",
          vc: selectedPbsRow.vc,
          asn: selectedPbsRow.asn,
          serialNumber: biwSerialNumber.trim(),
        };
        showLoading();
        await API.put(API_RESOURCE_URLS.updatePbsStatus(plant.value), payload);
        showPopup({
          type: MESSAGE_TYPE.SUCCESS,
          contextText: " ",
          info: "BIW Serial Number Validated Successfully",
        });

        const currentRow = selectedPbsRow;
        handlePbsDialogClose();

        if (currentRow) {
          await readyfordropRows([currentRow], true);
        } else {
          reloadData();
        }
      } catch (error) {
        showError({
          contextText: "Failed to Validate BIW Serial Number",
          error: error,
        });
      }
    }
  };

  const getContextualButtonClasses = (activeDate, isDropLocked) => {
    const isYesterday = moment().subtract(1, "days").isSame(activeDate, "day");
    const isToday = moment().isSame(activeDate, "day");
    if (isYesterday) return styles.greenRoundButton;
    if (isToday && isDropLocked) return styles.redRoundButton;
    return styles.greyRoundButton;
  };

  const fetchShiftAssignmentsFor = async (
    plantId,
    shopId,
    lineId,
    from,
    to,
  ) => {
    if (!plantId && !shopId && !lineId && !from && !to) return;
    const response = await API.get(
      API_RESOURCE_URLS.shiftAssignments(plantId, shopId, lineId, from, to),
    );
    return response.data;
  };

  const loadAvailableShiftAssignmentsFor = async (
    plantId,
    shopId,
    lineId,
    forDate,
  ) => {
    setIsLoading(true);
    let shiftsForDate = forDate?.format(DATE_FORMAT.ISO);
    if (forDate?.isBefore(moment().format(DATE_FORMAT.ISO))) {
      shiftsForDate = moment().format(DATE_FORMAT.ISO);
    }
    try {
      const activeShifts = await fetchShiftAssignmentsFor(
        plantId,
        shopId,
        lineId,
        shiftsForDate,
        shiftsForDate,
      );

      const shiftAvailabilityData = activeShifts.map((shift) => {
        return {
          label: shift.shiftName,
          value: shift.shiftId,
        };
      });
      const sortedShiftAvailabilityData = shiftAvailabilityData.sort((a, b) => {
        if (a.label < b.label) {
          return -1;
        }
        if (a.label > b.label) {
          return 1;
        }
        return 0;
      });
      await setShiftAvailability(sortedShiftAvailabilityData);
      setIsLoading(false);
    } catch (error) {
      console.error(buildErrorMessage(error));
      showInternalError({ error: error });
      setShiftAvailability([]);
    }
  };

  const loadShopIdForSelectedLine = async (lineId) => {
    try {
      const response = await API.get(
        API_RESOURCE_URLS.lineDetailsByLineId(lineId), // TODO: get from planning instead of digital twin service
      );
      const lineDetails = response.data;
      setShopId(lineDetails.shop?.id);
    } catch (error) {
      console.error(buildErrorMessage(error));
      showInternalError({ error: error });
      setShopId(null);
    }
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    setStylesAccordingToPlant(plant, setStyles, defaultStyles, jlrStyles);
  }, [plant]);

  useEffect(() => {
    if (line.value) {
      loadShopIdForSelectedLine(line.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line]);
  const [buSpecificColumns, setBuSpecificColumns] = useState([]);
  useEffect(() => {
    if (
      line.value &&
      activeAsnDate &&
      shopId &&
      plant.value &&
      isAssignShiftFormOpen
    ) {
      loadAvailableShiftAssignmentsFor(
        plant.value,
        shopId,
        line.value,
        activeAsnDate,
      );
    }
    if (APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4) {
      setBuSpecificColumns([...fixedColumns, ...dropTableColumns]);
    } else {
      setBuSpecificColumns([...fixedColumns]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, shopId, line, activeAsnDate, isAssignShiftFormOpen]);

  useEffect(() => {
    handleCountChange(asnCount);
  }, [asnCount, handleCountChange]);

  useEffect(() => {
    if (rowsData.loading) {
      showLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasButtonEnable]);

  useEffect(() => {
    latestLineValueRef.current = line.value;
    latestPlantValueRef.current = plant.value;

    setActiveAsnDate(null);
    loadAsnDates(
      plant.value,
      line.value,
      setAsnDates,
      setActiveAsnDate,
      null,
      latestPlantValueRef,
      latestLineValueRef,
    ).catch((err) => {
      //TODO: handleFailure
      console.error(buildErrorMessage(err));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value, line.value]);

  useEffect(() => {
    setHasActiveEdits(rowsData.data.some((row) => row.edited));
  }, [rowsData.data, setHasActiveEdits]);

  return (
    <>
      <div className={styles.backButton}>
        <BackButton
          basePath="PPC"
          action="Line Up"
          handleClick={redirectToApplicationLandingPage}
        />
      </div>

      <DropToolBar
        activeDate={activeAsnDate}
        dates={asnDates}
        handleDateSelect={(date) => {
          setActiveAsnDate(date);
          setEnableRDFButton(true);
        }}
        selectedOptionalColNames={selectedOptionalColNames}
        applyColumnsSelection={(selectedColName) =>
          setSelectedOptionalColNames(selectedColName)
        }
        lastRefreshedDate={lastRefreshedDate}
        contextualButtonProps={{
          className: getContextualButtonClasses(
            activeAsnDate,
            hasPendingBacklogAsns,
          ),
          text: "PREVIOUS",
          label: "Prev",
          handleClick: () => {
            setActiveAsnDate(moment().subtract(1, "days"));
            setEnableRDFButton(false);
          },
        }}
        restrictNavigationDuringEditing
        hasActiveEdits={hasActiveEdits}
        isPrintingEnabled={isPrintingEnabled}
        setIsPrintingEnabled={setIsPrintingEnabled}
        showPrintingToggle={plant.label === PANTNAGAR_PLANT_NAME}
      />
      <BulkOptionsToolbar
        line={line}
        activeAsnDate={activeAsnDate}
        selectedAsns={selectedAsns}
        asns={rowsData.data}
        reloadData={reloadData}
        isLoading={rowsData.loading}
        handleClose={closeAssignShiftForm}
        closeNextMonthVinForm={closeNextMonthVinForm}
        closeAddTyerAndOthers={closeAddTyerAndOthers}
        assignShift={assignShift}
        nextMonthVINRequest={nextMonthVINRequest}
        activeTyreAndOthersRequest={activeTyreAndOthersRequest}
        bulkAssignShift={bulkAssignShift}
        isAssignShiftFormOpen={isAssignShiftFormOpen}
        isNextMonthFormOpen={isNextMonthFormOpen}
        isAddTyerAndOthersOpen={isAddTyerAndOthersOpen}
        shifts={shiftAvailability}
        isAssignShiftFormDataLoading={isLoading}
      />
      {isLineOutModalOpen ? (
        <CommentFormModal
          open={isLineOutModalOpen}
          handleClose={closeModal}
          title="Line-Out"
          rowsData={selectedRows}
          lineBackgroundColor="#82C1A7"
          reasons={reasons}
          agencies={agencies}
          handleSubmit={(rows, reason, agency, comment) => {
            lineoutRows(rows, reason, agency, comment);
          }}
        />
      ) : null}

      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: "100%" }}>
          <Table
            columns={buSpecificColumns}
            defaultColumns={[...fixedColumns]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.filteredData}
            primaryAction={{
              name: "READY FOR DROP",
              actionFn: readyfordropRows,
              authOperation: RESOURCE_OPERATION,
              shouldEnable: (selectedRows) => {
                const futureDateRFDButtonDisable =
                  activeAsnDate && activeAsnDate.isAfter(moment(), "day");
                return (
                  !futureDateRFDButtonDisable &&
                  selectedRows.length &&
                  selectedRows.every(
                    (row) => row.status === ASN_STATUS.PLANNED,
                  ) &&
                  checkForPendingAsns(selectedRows) &&
                  !hasPendingBacklogAsns
                );
              },
            }}
            secondaryActions={[
              {
                name: "Line-out",
                actionFn: (selectedRows) => openModal(selectedRows),
                backgroundColor: "#F17C7C",
                authOperation: RESOURCE_OPERATION,
                shouldEnable: (selectedRows) =>
                  selectedRows.length &&
                  selectedRows.every(
                    (row) => row.status === ASN_STATUS.PLANNED,
                  ),
              },
              {
                name: "PULLBACK",
                actionFn: (selectedRows) => openPullBackModal(selectedRows),
                authOperation: USER_OPERATIONS.UPDATE_CHASSIS_PLAN_ASN,
                shouldEnable: (selectedRows) => {
                  return (
                    selectedRows.length &&
                    rowsData?.data[0]?.status === ASN_STATUS.PLANNED &&
                    selectedRows.every(
                      (row) => row.status === ASN_STATUS.PLANNED,
                    ) &&
                    checkForPendingAgsnsOrAsn(
                      rowsData.data,
                      selectedRows,
                      activeAsnDate,
                      ASN_STATUS.PLANNED,
                    )
                  );
                },
              },
              {
                name: "ASSIGN SHIFT",
                actionFn: (selectedRows) => openAssignShiftForm(selectedRows),
                backgroundColor: plant.label === JLR_PLANT && "#FFFFFF",
                textColor: plant.label === JLR_PLANT && "#6AA98F",
                authOperation: RESOURCE_OPERATION,
                shouldEnable: () => rowsData.data.length || activeAsnDate,
              },
              {
                name: "DOWNLOAD CSV",
                getFilteredData: true,
                authOperation: USER_OPERATIONS.GET_CHASSIS_PLAN_ASN,
                backgroundColor: plant.label === JLR_PLANT && "#FFFFFF",
                textColor: plant.label === JLR_PLANT && "#6AA98F",
                shouldEnable: () => rowsData.data.length > 0,
                actionFn: (filteredData) =>
                  generateAndDownloadAsnCsvReport(filteredData),
              },
              {
                name: "CARRY FORWARD",
                authOperation: RESOURCE_OPERATION,
                shouldEnable: () => {
                  const allPlannedAsns = rowsData.data.filter(
                    (asn) => asn.status === ASN_STATUS.PLANNED,
                  );
                  if (allPlannedAsns.length > 0) return true;
                },
                backgroundColor: plant.label === JLR_PLANT && "#FFFFFF",
                textColor: plant.label === JLR_PLANT && "#6AA98F",
                actionFn: (selectedRows) => handleCarryForwardAsn(selectedRows),
              },
              {
                name: "LINE TRANSFER",
                actionFn: (selectedRows) => openLineTransferModal(selectedRows),
                backgroundColor: "#608fc9",
                authOperation: RESOURCE_OPERATION,
                shouldEnable: () => rowsData.data.length || activeAsnDate,
              },
              {
                name: `NEXT MONTH ${nextMonthLabel}`,
                actionFn: (selectedRows) => openNextMonthForm(selectedRows),
                authOperation: USER_OPERATIONS.CREATE_CHASSIS_PLAN_ASN,
                shouldEnable: (selectedRows) => {
                  const futureDateRFDButtonDisable =
                    activeAsnDate && activeAsnDate.isAfter(moment(), "day");
                  return (
                    selectedRows.length &&
                    selectedRows.every(
                      (row) => row.status === ASN_STATUS.PLANNED,
                    )
                  );
                },
              },
              {
                name: "Add Tyre and Other",
                actionFn: (selectedRows) => openAddTyerAndOthers(selectedRows),
                authOperation: USER_OPERATIONS.CREATE_CHASSIS_PLAN_ASN,
                shouldEnable: (selectedRows) => {
                  return (
                    selectedRows.length &&
                    selectedRows.every(
                      (row) => row.status === ASN_STATUS.PLANNED,
                    )
                  );
                },
              },
              ...(plant.label === PANTNAGAR_PLANT_NAME
                ? [
                    {
                      name: "Reprint Barcode",
                      actionFn: reprintRows,
                      authOperation: RESOURCE_OPERATION,
                      shouldEnable: (selectedRows) =>
                        isPrintingEnabled &&
                        selectedRows.length > 0 &&
                        selectedRows.every(
                          (row) =>
                            row.status === ASN_STATUS.DROPPED ||
                            row.status === ASN_STATUS.READY_FOR_DROP,
                        ),
                    },
                  ]
                : []),
            ]}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={setCount}
            isDataLoading={rowsData.loading}
            onRowSelection={handleRowSelection}
          />
        </div>
      </div>
      <AggregateAvailableErrorPopup
        open={isPlanErrorsOpen}
        handleClose={() => setIsPlanErrorsOpen(false)}
        errors={uploadErrors}
      />
      {isPullbackModalOpen ? (
        <CommentFormModal
          open={isPullbackModalOpen}
          handleClose={closePullBackModal}
          title="Pullback"
          rowsData={selectedRows}
          reasons={reasons}
          agencies={agencies}
          lineBackgroundColor="#82C1A7"
          handleSubmit={(rows, reason, agency, comment) => {
            pullBackAsn(rows, reason, agency, comment);
          }}
        />
      ) : null}
      {isCarryForwardModalOpen ? (
        <CarryForwardModal
          open={isCarryForwardModalOpen}
          asnCarryForwardCount={carryForwardAsnCount}
          handleClose={closeCarryForwardModal}
          rowsData={carryForwardAsnDetails}
          handleSubmit={handleSubmitCarryForwardAsn}
        />
      ) : null}
      {isLineTransferModalOpen ? (
        <LineTransferModal
          open={isLineTransferModalOpen}
          handleClose={closeLineTransferModal}
          title="Line-Transfer"
          lineBackgroundColor="#82C1A7"
          lines={transferLines.map((line) => line.lineName)}
          handleSubmit={handleLineSelectionForLineTransfer}
        />
      ) : null}
      {isLineTransferAsnDisplayOpen ? (
        <DayWiseFinishedGoodsLineTransferDisplay
          open={isLineTransferAsnDisplayOpen}
          handleClose={closeLinetransferAnsDisplay}
          updateRows={reloadData}
          startDate={activeAsnDate}
          endDate={activeAsnDate}
          plant={plant.value}
          line={line.value}
          toline={lineSelectedForTransfer.line}
          lineName={lineSelectedForTransfer.lineName}
        />
      ) : null}
      <ReprintWarningDialog
        open={isReprintConfirmationOpen}
        onCancel={handleCancelReprint}
        onConfirm={handleConfirmReprint}
      />

      {isRfdValidationEnabled && (
        <Modal
          open={openPbsDialog}
          onClose={handlePbsDialogClose}
          disableEnforceFocus
          disableAutoFocus
        >
          <div
            className={styles.popupContainer}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className={styles.popupTitle}>
              <span>
                <EditIcon className={styles.popupIcon} />
              </span>
              <span style={{ marginLeft: "10px" }}>BIW Validation</span>
            </div>
            <Box
              bgcolor="#dadada3d"
              marginTop="33px"
              className={styles.popupContent}
            >
              {selectedPbsRow && (
                <>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span className={styles.popupLabel}>
                      VC: {selectedPbsRow.vc}
                    </span>
                    <span className={styles.popupLabel}>
                      ASN: {selectedPbsRow.asn}
                    </span>
                  </div>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="BIW Serial Number"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={biwSerialNumber}
                    onChange={(e) => setBiwSerialNumber(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (vcInputRef.current) {
                          vcInputRef.current.focus();
                        }
                      }
                    }}
                    style={{ backgroundColor: "white" }}
                  />
                  <TextField
                    inputRef={vcInputRef}
                    margin="dense"
                    label="Scan VC"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={scannedVc}
                    onChange={(e) => setScannedVc(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handlePbsSubmit();
                      }
                    }}
                    style={{ backgroundColor: "white", marginTop: "10px" }}
                  />
                </>
              )}
            </Box>
            <div className={styles.popupBottomRow}>
              <button
                className={styles.popupCancelBt}
                onClick={handlePbsDialogClose}
              >
                Cancel
              </button>
              <button
                onClick={handlePbsSubmit}
                className={styles.popupSubmitBt}
              >
                Submit
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

ChassisDrop.propTypes = {
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
};

export default withAllowedOperationsProvider(
  ChassisDrop,
  false,
  RESOURCE_TYPE.CHASSIS,
);
